import { useMemo, useState } from 'react';
import { Baby, Syringe, Activity, Calculator, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * PediatricReference — age + weight-based vitals and drug-dose reference.
 *
 * Two panels in one component:
 *
 *   1. PediatricVitalsReference
 *      Normal HR / RR / SBP ranges by age bracket. Follows PALS 2020 /
 *      AAP ranges. The user's patient's age (or estimated weight) is
 *      used to highlight the active bracket so the crew can glance and
 *      know what "normal" means for THIS kid without doing math.
 *
 *   2. PediatricDoseCalculator
 *      Weight-based dosing for the most commonly field-administered
 *      pediatric drugs. If weight is known we compute the exact mg /
 *      mcg / mL. If only age is known we use the Broselow-equivalent
 *      weight estimate ( (age + 4) × 2 for 1-10y ) and flag the value
 *      as estimated so the provider knows to confirm.
 *
 * CLINICAL SAFETY NOTE (also on every card): these are REFERENCES, not
 * orders. Always confirm dose with a 2nd provider and med control when
 * required by the agency's protocol.
 */

interface VitalsBand {
  /** Min age in months (inclusive). */
  minMonths: number;
  /** Max age in months (inclusive). 12*18 = 18y. */
  maxMonths: number;
  label: string;
  hrLow: number; hrHigh: number;
  rrLow: number; rrHigh: number;
  sbpLow: number; sbpHigh: number;
}

const VITALS_BANDS: VitalsBand[] = [
  // PALS + AAP reference ranges
  { minMonths: 0,   maxMonths: 0,    label: 'Newborn (0 d)',        hrLow: 100, hrHigh: 205, rrLow: 30, rrHigh: 60, sbpLow: 60, sbpHigh: 90  },
  { minMonths: 1,   maxMonths: 11,   label: 'Infant (1–11 mo)',     hrLow: 100, hrHigh: 190, rrLow: 30, rrHigh: 60, sbpLow: 70, sbpHigh: 100 },
  { minMonths: 12,  maxMonths: 23,   label: 'Toddler (1 yr)',       hrLow: 98,  hrHigh: 140, rrLow: 22, rrHigh: 37, sbpLow: 86, sbpHigh: 106 },
  { minMonths: 24,  maxMonths: 59,   label: 'Preschool (2–4 yr)',   hrLow: 80,  hrHigh: 120, rrLow: 20, rrHigh: 28, sbpLow: 89, sbpHigh: 112 },
  { minMonths: 60,  maxMonths: 107,  label: 'School (5–8 yr)',      hrLow: 75,  hrHigh: 118, rrLow: 18, rrHigh: 25, sbpLow: 97, sbpHigh: 115 },
  { minMonths: 108, maxMonths: 143,  label: 'Preteen (9–11 yr)',    hrLow: 60,  hrHigh: 110, rrLow: 15, rrHigh: 22, sbpLow: 102, sbpHigh: 120 },
  { minMonths: 144, maxMonths: 215,  label: 'Adolescent (12–17 yr)', hrLow: 60, hrHigh: 100, rrLow: 12, rrHigh: 20, sbpLow: 110, sbpHigh: 131 },
];

/** Estimate weight in kg using the APLS formula for 1–10 y. */
function estimateWeightKg(ageYears: number): number {
  if (ageYears < 1) return 3.5 + ageYears * (10 - 3.5); // 3.5 kg newborn → ~10 kg at 12 mo
  if (ageYears <= 10) return 2 * (ageYears + 4);        // APLS
  // Over 10, extrapolate based on adolescent median; clinical practice uses
  // actual measured weight by that age so this is a fallback only.
  return 30 + (ageYears - 10) * 5;
}

function ageYearsFromDob(dob: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return ms / (365.25 * 24 * 3600 * 1000);
}
function ageMonthsFromDob(dob: string | null): number | null {
  const yrs = ageYearsFromDob(dob);
  return yrs === null ? null : Math.round(yrs * 12);
}

// ============================================================
// Public card — combines vitals + dosing.
// Renders nothing if the patient isn't pediatric (age > 17).
// ============================================================
interface Props {
  dob: string | null;                  // ISO date
  weightKg?: number | null;            // if documented
}

export function PediatricReference({ dob, weightKg }: Props) {
  const months = ageMonthsFromDob(dob);
  if (months === null || months > 215) return null; // not pediatric

  const ageYears = months / 12;
  const isEstimatedWeight = weightKg == null || weightKg <= 0;
  const effectiveKg = weightKg && weightKg > 0 ? weightKg : Math.round(estimateWeightKg(ageYears) * 10) / 10;
  const band = VITALS_BANDS.find((b) => months >= b.minMonths && months <= b.maxMonths) ?? VITALS_BANDS[VITALS_BANDS.length - 1];

  return (
    <section className="border border-amber-700/80 bg-amber-950/20">
      <div className="flex items-center justify-between border-b border-amber-700/60 bg-amber-950/40 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
          <Baby size={13} />
          Pediatric Reference
        </div>
        <div className="text-[10px] font-mono text-amber-300">
          age {formatAge(months)}
          {' · '}
          <span className={isEstimatedWeight ? 'text-amber-400' : 'text-emerald-300'}>
            {effectiveKg} kg{isEstimatedWeight ? ' (est)' : ''}
          </span>
        </div>
      </div>
      <VitalsTable band={band} />
      <DoseTable weightKg={effectiveKg} estimated={isEstimatedWeight} />
      <div className="border-t border-amber-900/60 px-2.5 py-1 text-[9px] text-amber-400/80 italic">
        Reference only — not an order. Confirm dose with a 2nd provider / med control per protocol. Doses reflect typical EMS field limits; refer to RIEMSIS / RI DOH protocols for the full package.
      </div>
    </section>
  );
}

// ============================================================
// Vitals table
// ============================================================
function VitalsTable({ band }: { band: VitalsBand }) {
  return (
    <div className="border-b border-amber-900/40">
      <div className="grid grid-cols-4 divide-x divide-amber-900/50 text-[11px]">
        <div className="p-1.5">
          <div className="data-label-dense text-amber-500">Bracket</div>
          <div className="text-amber-100 font-semibold">{band.label}</div>
        </div>
        <div className="p-1.5">
          <div className="data-label-dense text-amber-500">HR (bpm)</div>
          <div className="text-amber-100 font-mono tabular-nums">{band.hrLow}–{band.hrHigh}</div>
        </div>
        <div className="p-1.5">
          <div className="data-label-dense text-amber-500">RR (/min)</div>
          <div className="text-amber-100 font-mono tabular-nums">{band.rrLow}–{band.rrHigh}</div>
        </div>
        <div className="p-1.5">
          <div className="data-label-dense text-amber-500">SBP (mmHg)</div>
          <div className="text-amber-100 font-mono tabular-nums">{band.sbpLow}–{band.sbpHigh}</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Dose table
// ============================================================
interface DoseDef {
  name: string;
  category: 'Resus' | 'Airway' | 'Seizure' | 'Analgesic' | 'Antipyretic' | 'Anaphylaxis' | 'Hypoglycemia';
  /** Rendered line of exact dose as a function of weight. */
  compute: (kg: number) => { mg?: string; mcg?: string; mL?: string; note?: string };
  /** Max single dose in mg (for capping — avoids adult-overdose at high pediatric weights). */
  capMg?: number;
  /** Concentration used for mL math (if applicable). */
  conc?: string;
}

const DOSES: DoseDef[] = [
  {
    name: 'Epinephrine 1:10,000 (IV/IO — Arrest)',
    category: 'Resus',
    conc: '0.1 mg/mL',
    capMg: 1,
    compute: (kg) => {
      const mg = Math.min(0.01 * kg, 1);
      const mL = mg / 0.1; // 0.1 mg/mL
      return { mg: `${mg.toFixed(2)} mg`, mL: `${mL.toFixed(1)} mL`, note: 'Repeat q 3–5 min' };
    },
  },
  {
    name: 'Epinephrine 1:1,000 IM (Anaphylaxis)',
    category: 'Anaphylaxis',
    conc: '1 mg/mL',
    capMg: 0.3,
    compute: (kg) => {
      const mg = Math.min(0.01 * kg, 0.3);
      const mL = mg / 1;
      return { mg: `${mg.toFixed(2)} mg`, mL: `${mL.toFixed(2)} mL`, note: 'Lateral thigh · repeat q 5 min prn' };
    },
  },
  {
    name: 'Naloxone (IN/IV/IM)',
    category: 'Resus',
    conc: '1 mg/mL',
    capMg: 2,
    compute: (kg) => {
      const mg = Math.min(0.1 * kg, 2);
      return { mg: `${mg.toFixed(2)} mg`, note: 'Titrate to adequate respirations; half-dose if chronic-opioid patient' };
    },
  },
  {
    name: 'Atropine (Symptomatic Brady)',
    category: 'Resus',
    conc: '0.1 mg/mL',
    capMg: 0.5,
    compute: (kg) => {
      const raw = 0.02 * kg;
      const mg = Math.max(0.1, Math.min(raw, 0.5));  // min 0.1, max 0.5
      const mL = mg / 0.1;
      return { mg: `${mg.toFixed(2)} mg`, mL: `${mL.toFixed(1)} mL`, note: 'Min 0.1 mg · max 0.5 mg single dose' };
    },
  },
  {
    name: 'Midazolam IN/IM (Seizure)',
    category: 'Seizure',
    conc: '5 mg/mL',
    capMg: 10,
    compute: (kg) => {
      const mg = Math.min(0.2 * kg, 10);
      const mL = mg / 5;
      return { mg: `${mg.toFixed(1)} mg`, mL: `${mL.toFixed(2)} mL`, note: '0.2 mg/kg IN split between nares' };
    },
  },
  {
    name: 'Albuterol (Neb)',
    category: 'Airway',
    compute: (kg) => {
      if (kg < 20) return { mg: '2.5 mg', mL: '3 mL unit dose', note: 'Under 20 kg — 2.5 mg nebulized' };
      return { mg: '5 mg', mL: '2 × 2.5 mg', note: '≥ 20 kg — 5 mg nebulized' };
    },
  },
  {
    name: 'D10 (Hypoglycemia)',
    category: 'Hypoglycemia',
    conc: '0.1 g/mL',
    compute: (kg) => {
      const g = Math.min(0.5 * kg, 25); // 0.5 g/kg capped at 25 g
      const mL = g / 0.1;
      return { mg: `${g.toFixed(1)} g`, mL: `${mL.toFixed(0)} mL D10`, note: 'Re-check BG after 10 min' };
    },
  },
  {
    name: 'Diphenhydramine (Allergic Rxn)',
    category: 'Anaphylaxis',
    conc: '50 mg/mL',
    capMg: 50,
    compute: (kg) => {
      const mg = Math.min(1 * kg, 50);
      const mL = mg / 50;
      return { mg: `${mg.toFixed(0)} mg`, mL: `${mL.toFixed(2)} mL IM/IV`, note: '1 mg/kg max 50 mg' };
    },
  },
  {
    name: 'Acetaminophen PO (Pain/Fever)',
    category: 'Analgesic',
    capMg: 1000,
    compute: (kg) => {
      const mg = Math.min(15 * kg, 1000);
      return { mg: `${mg.toFixed(0)} mg PO`, note: 'Weight-based 15 mg/kg · max 1000 mg' };
    },
  },
  {
    name: 'Ibuprofen PO (Pain/Fever > 6 mo)',
    category: 'Antipyretic',
    capMg: 400,
    compute: (kg) => {
      const mg = Math.min(10 * kg, 400);
      return { mg: `${mg.toFixed(0)} mg PO`, note: '10 mg/kg · max 400 mg · avoid < 6 mo' };
    },
  },
];

function DoseTable({ weightKg, estimated }: { weightKg: number; estimated: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const computed = useMemo(
    () => DOSES.map((d) => ({ def: d, result: d.compute(weightKg) })),
    [weightKg],
  );
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left bg-amber-950/30 hover:bg-amber-950/50 border-b border-amber-900/40"
      >
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
          <Calculator size={12} />
          Dose Reference · weight-based
          {estimated && <span className="text-[9px] text-amber-400 font-mono normal-case tracking-normal">(est weight)</span>}
        </span>
        {expanded ? <ChevronDown size={13} className="text-amber-400" /> : <ChevronRight size={13} className="text-amber-400" />}
      </button>
      {expanded && (
        <div className="divide-y divide-amber-900/40">
          {computed.map(({ def, result }) => (
            <div key={def.name} className="grid grid-cols-[1fr_auto] gap-2 px-2.5 py-1.5 text-[11px]">
              <div>
                <div className="text-amber-100 font-semibold">{def.name}</div>
                {result.note && <div className="text-amber-400/90 text-[10px] mt-0.5">{result.note}</div>}
              </div>
              <div className="text-right font-mono text-amber-100 tabular-nums shrink-0">
                {result.mg && <div className="font-bold">{result.mg}</div>}
                {result.mL && <div className="text-[10px] text-amber-300">{result.mL}</div>}
                {def.conc && <div className="text-[9px] text-amber-500/80">{def.conc}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAge(months: number): string {
  if (months < 1) return '< 1 mo';
  if (months < 24) return `${months} mo`;
  const yrs = Math.floor(months / 12);
  const rem = months % 12;
  return rem ? `${yrs} y ${rem} mo` : `${yrs} y`;
}
